import React, { useState, useEffect } from 'react'
import { connect } from 'react-redux'
import { Field, Form } from 'react-final-form'
import { Map } from 'immutable'
import IconBack from '@material-ui/icons/ArrowBack'
import { injectIntl } from 'react-intl'
import {
  IconButton,
  Button,
  FormControl,
  FormControlLabel,
  Typography,
  Switch,
} from '@material-ui/core'
import { licenseeFetchList } from 'reducers/licensee/reducer'
import Psfs from 'lib/psfs/Psfs'
import { API_URL_LICENSEE_LIST, FEATURE_FLAGS_KEY } from 'constants/index'
import SelectInput from 'components/common/Inputs/SelectInput'
import Input from 'components/common/Form/Input'
import {
  fetchFeatureFlagsForm,
  clearFeatureFlagsForm,
  setFeatureFlagsInfo,
} from 'reducers/featureFlags/reducer'
import Loader from 'components/Loading'
import { FeatureFlag, Licensee } from 'types/featureFlags'
import withPromise from 'helpers/withPromise'
import { FormBasic } from 'components/common/styles'
import { FeatureFlagFormProps } from './FeatureFlagForm.types'
import { FormContainer, FieldWrapper } from './FeatureFlagForm.styles'
import messages from '../translate'

export const FeatureFlagsForm = ({
  clearFeatureFlagsForm,
  fetchFeatureFlagsForm,
  licenseeFetchList,
  setFeatureFlagsInfo,
  match: {
    params: { id: featureFlagId },
  },
  isLoadForm,
  formData,
  intl: { formatMessage },
  history: { goBack },
}: FeatureFlagFormProps) => {
  const [licenseesOptions, setLicenseesOptions] = useState([])

  useEffect(() => {
    withPromise(licenseeFetchList, {
      psfs: Psfs.create(API_URL_LICENSEE_LIST)
        .createPagination(1, 'all')
        .addFields(['id', 'name']),
    }).then(data => setLicenseesOptions(data))

    fetchFeatureFlagsForm({
      entityId: featureFlagId,
    })

    return () => {
      clearFeatureFlagsForm()
    }
  }, [])

  const onSubmit = ({
    licensees,
    title = '',
    description = '',
    enabled,
  }: FeatureFlag) =>
    withPromise(setFeatureFlagsInfo, {
      entityId: featureFlagId,
      values: {
        licensees,
        title,
        description,
        enabled,
      },
    }).then(errors => (errors ? Promise.resolve(errors) : goBack()))

  const formValue = formData.toJS()

  if (isLoadForm || !formValue.id) {
    return <Loader pastDelay />
  }

  return (
    <FormContainer>
      <Typography variant="h5">
        <IconButton size="medium" onClick={goBack}>
          <IconBack aria-label="edit back button" fontSize="small" />
        </IconButton>
        Feature flag info
      </Typography>
      <Form
        onSubmit={onSubmit}
        initialValues={{
          ...formValue,
          licensees: formValue.licensees.map(({ id }: Licensee) => id),
        }}
        render={({ handleSubmit, submitting, pristine }) => (
          <FormBasic onSubmit={handleSubmit}>
            <Field
              name="key"
              type="text"
              label={formatMessage(messages.key)}
              component={Input}
              disabled
            />
            <Field
              name="title"
              label={formatMessage(messages.title)}
              type="text"
              component={Input}
            />
            <Field
              name="description"
              label={formatMessage(messages.description)}
              type="text"
              component={Input}
            />
            <FormControl fullWidth component={FieldWrapper}>
              <Field
                name="licensees"
                render={({ input }) => (
                  <SelectInput
                    hasSearchField
                    hasSelectAllOption
                    multiple
                    label="Choose licensees"
                    options={licenseesOptions.map(({ id, name }) => ({
                      value: id,
                      label: name,
                    }))}
                    {...input}
                  />
                )}
              />
            </FormControl>
            <FormControl fullWidth>
              <FormControlLabel
                label="Enabled"
                disabled={submitting}
                control={
                  <Field
                    name="enabled"
                    type="checkbox"
                    render={({ input }) => (
                      <Switch color="primary" {...input} />
                    )}
                  />
                }
              />
            </FormControl>
            <Button
              type="submit"
              color="primary"
              size="medium"
              variant="contained"
              onClick={handleSubmit}
              disabled={submitting || pristine}>
              {formatMessage(messages.save)}
            </Button>
          </FormBasic>
        )}
      />
    </FormContainer>
  )
}

export default connect(
  (state: {
    getIn: (path: Array<string>) => Map<string, FeatureFlag> | boolean
  }) => ({
    isLoadForm: state.getIn([FEATURE_FLAGS_KEY, 'isLoadForm']) as boolean,
    formData: state.getIn([FEATURE_FLAGS_KEY, 'formData']) as Map<
      string,
      FeatureFlag
    >,
  }),
  {
    fetchFeatureFlagsForm,
    setFeatureFlagsInfo,
    clearFeatureFlagsForm,
    licenseeFetchList,
  },
)(injectIntl(FeatureFlagsForm))
